import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { postContact } from "../lib/api";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/motion";

const EMPTY = { name: "", email: "", subject: "", audience: "general", message: "" };

export default function Contact() {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true); setStatus(null);
    try {
      await postContact(form);
      setStatus("ok");
      setForm(EMPTY);
    } catch {
      setStatus("err");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="page-contact">
      <PageHero
        testId="contact-hero"
        kicker={t("contact.kicker")}
        title={t("contact.headline")}
        lead={t("contact.lead")}
        aside={
          <div className="paper p-8 lg:mt-20" data-testid="contact-info-card">
            <div className="overline text-gold">{t("contact.email_label")}</div>
            <a href={`mailto:${t("contact.email_value")}`} className="flex items-center gap-3 mt-5 font-display text-xl lg:text-2xl text-forest gxr-link" data-testid="contact-email-link">
              <Mail className="h-5 w-5 text-gold" />
              {t("contact.email_value")}
            </a>
          </div>
        }
      />

      <section className="surface-2" data-testid="contact-form-section">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <Reveal>
            <form onSubmit={onSubmit} className="paper p-8 lg:p-14 space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <label className="gxr-label" htmlFor="c-name">{t("contact.form.name")} *</label>
                  <input id="c-name" required value={form.name} onChange={set("name")} className="gxr-input" data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="c-email">{t("contact.form.email")} *</label>
                  <input id="c-email" type="email" required value={form.email} onChange={set("email")} className="gxr-input" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="c-audience">{t("contact.form.audience")}</label>
                  <select id="c-audience" value={form.audience} onChange={set("audience")} className="gxr-input bg-transparent" data-testid="contact-audience-select">
                    {["general", "collaborator", "professional"].map((k) => (
                      <option key={k} value={k}>{t(`contact.form.audience_options.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="gxr-label" htmlFor="c-subject">{t("contact.form.subject")} *</label>
                  <input id="c-subject" required value={form.subject} onChange={set("subject")} className="gxr-input" data-testid="contact-subject-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="gxr-label" htmlFor="c-message">{t("contact.form.message")} *</label>
                  <textarea id="c-message" required rows={6} value={form.message} onChange={set("message")} className="gxr-input resize-y" data-testid="contact-message-input" />
                </div>
              </div>

              <div className="flex items-center gap-4 pt-2 flex-wrap">
                <button type="submit" disabled={submitting} className="btn-forest" data-testid="contact-submit-button">
                  {submitting ? t("common.sending") : t("common.submit")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </button>
                {status === "ok" && (
                  <div className="flex items-center gap-2 text-forest" data-testid="contact-success"><CheckCircle2 className="h-5 w-5" /> {t("common.thankYou")}</div>
                )}
                {status === "err" && (
                  <div className="flex items-center gap-2 text-red-600" data-testid="contact-error"><AlertCircle className="h-5 w-5" /> {t("common.errorGeneric")}</div>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
